import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useJdShopAuthentication } from './hooks';
import { Button, Popconfirm, Space, message } from 'udesk-ui';
import Page from 'Component/common/udesk-qa-page-layout';
import CommonTablePage from 'Component/common/udesk-qa-table-page';
import type { TableComponentProps } from 'Component/common/udesk-qa-table-page';
import ConfigForm, { TemplateProps as ConfigFormProps } from './components/ConfigForm';
import { getIntegrationJd } from 'src/api/integration/jd';
import { deleteIntegrationJdById } from 'src/api/integration/jd/{id}';
import { JdShopAuthURL } from './const';
import './style.scss';

const Template = React.memo((props: any) => {
    const [getLoading, setGetLoading] = useState(false);
    const [dataSource, setDataSource] = useState<any[]>([]);
    const [paging, setPaging] = useState({
        pageNum: 1,
        pageSize: 20,
        total: 0,
    });
    const ref = useRef<any>({});

    const queryList = useCallback(
        ({ pageSize, pageNum }: { pageSize: number; pageNum: number }) => {
            setGetLoading(true);
            getIntegrationJd({
                params: {
                    pageNum,
                    pageSize,
                },
            }).then((resp) => {
                setDataSource(resp.data || []);
                setGetLoading(false);
                setPaging(resp.paging as any);
            });
        },
        []
    );

    // 如果是授权跳转过来的，授权结束后再请求列表
    const { isAuth, loading: authLoading } = useJdShopAuthentication({
        onFinally: () => {
            queryList({ pageNum: 1, pageSize: 20 });
        },
    });

    // 如果不是授权跳转过来的，则直接请求列表
    useEffect(() => {
        if (!isAuth) {
            queryList({ pageSize: 20, pageNum: 1 });
        }
    }, [isAuth, queryList]);

    const paginationProps = useMemo(() => {
        const { pageSize, total } = paging;
        return {
            current: paging.pageNum,
            pageSize,
            total,
            onChange: (page, pageSize) => {
                const newPages = {
                    ...paging,
                    pageNum: page,
                    pageSize,
                };
                queryList({ ...newPages });
                setPaging(newPages);
            },
        };
    }, [paging, queryList]);

    const [delLoading, setDelLoading] = useState(false);
    const [shopId, setShopId] = useState<number>();
    const [mode, setMode] = useState<ConfigFormProps['mode']>();
    const [visible, setVisible] = useState(false);

    const columns = useMemo(
        () => [
            {
                width: '20%',
                title: /* 店铺名称 */ UdeskLocales['current'].pages.systemIntegration.jdShop.index
                    .shopName,
                dataIndex: 'shopName',
                key: 'shopName',
            },
            {
                width: '15%',
                key: 'shopId',
                dataIndex: 'shopId',
                title: /* 商家ID */ UdeskLocales['current'].pages.systemIntegration.jdShop.index
                    .merchantID,
            },
            {
                width: '15%',
                key: 'userName',
                dataIndex: 'userName',
                title: /* 账号 */ UdeskLocales['current'].pages.systemIntegration.jdShop.index
                    .accountNumber,
            },
            {
                key: 'status',
                dataIndex: 'status',
                width: '15%',
                title: /* 状态 */ UdeskLocales['current'].pages.systemIntegration.jdShop.index
                    .status,
                render: (v) => {
                    return v === 1
                        ? /* 已授权 */ UdeskLocales['current'].pages.systemIntegration.jdShop.index
                              .authorized
                        : v === 2
                        ? /* 订阅过期 */ UdeskLocales['current'].pages.systemIntegration.jdShop
                              .index.subscriptionExpiration
                        : /* 未授权 */ UdeskLocales['current'].pages.systemIntegration.jdShop.index
                              .unauthorized;
                },
            },
            {
                width: '15%',
                title: /* 创建时间 */ UdeskLocales['current'].pages.systemIntegration.jdShop.index
                    .creationTime,
                dataIndex: 'createTime',
                key: 'createTime',
            },
            {
                dataIndex: 'actions',
                title: UdeskLocales['current'].labels.action,
                render: (v, item) => {
                    const onEdit = (item) => {
                        setMode('edit');
                        setShopId(item.id);
                        setVisible(true);
                    };

                    const onAuth = (item) => {
                        window.open(JdShopAuthURL, '_blank');
                    };

                    const onDelete = (item) => {
                        setDelLoading(true);
                        deleteIntegrationJdById({
                            segments: {
                                id: item.id,
                            },
                        })
                            .then(() => {
                                message.success(
                                    UdeskLocales['current'].business.notifyMessage.deleteSuccess
                                );
                                queryList({
                                    pageNum: 1,
                                    pageSize: paging.pageSize,
                                });
                            })
                            .finally(() => {
                                setDelLoading(false);
                            });
                    };

                    return (
                        <Space>
                            <Button onClick={() => onEdit(item)} type="link">
                                {/* 编辑基础信息 */}
                                {
                                    UdeskLocales['current'].pages.systemIntegration.jdShop.index
                                        .editBasicInformation
                                }
                            </Button>

                            {item.status !== 1 ? (
                                <Button onClick={() => onAuth(item)} type="link">
                                    {item.status === 2
                                        ? /* 重新授权 */ UdeskLocales['current'].pages
                                              .systemIntegration.jdShop.index.reauthorization
                                        : /* 授权 */ UdeskLocales['current'].pages.systemIntegration
                                              .jdShop.index.authorization}
                                </Button>
                            ) : null}

                            <Popconfirm
                                onConfirm={() => onDelete(item)}
                                title={
                                    /* 确认删除吗？ */ UdeskLocales['current'].pages
                                        .systemIntegration.jdShop.index.confirmDeletion
                                }
                            >
                                <Button type="text" danger>
                                    {/* 删除 */}
                                    {
                                        UdeskLocales['current'].pages.systemIntegration.jdShop.index
                                            .delete
                                    }
                                </Button>
                            </Popconfirm>
                        </Space>
                    );
                },
            },
        ],
        [paging.pageSize, queryList]
    );

    const tableProps = useMemo<TableComponentProps>(() => {
        return {
            columns,
            dataSource,
            cacheKey: false,
        };
    }, [columns, dataSource]);

    const loading = authLoading || getLoading || delLoading;

    return (
        <Page pageBodyClassName="knowledge-base-page-index" padding={true}>
            <CommonTablePage
                ref={ref}
                loading={loading}
                mainButton={
                    <Button
                        onClick={() => {
                            setMode('create');
                            setShopId(undefined);
                            setVisible(true);
                        }}
                    >
                        {UdeskLocales['current'].labels.create}
                    </Button>
                }
            >
                <CommonTablePage.Table {...tableProps}></CommonTablePage.Table>
                <CommonTablePage.Pagination {...paginationProps}></CommonTablePage.Pagination>
            </CommonTablePage>

            <ConfigForm
                visible={visible}
                mode={mode}
                shopId={shopId}
                onSaveSuccess={() => {
                    setVisible(false);
                    queryList({ pageSize: 20, pageNum: 1 });
                }}
                onCancel={() => setVisible(false)}
            />
        </Page>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
